import { isAfter, isEqual, parseISO } from 'date-fns';
import { Sorting } from '../../../enums';
import { getDurationInSeconds, getFlightDuration } from '../../../utils';
import { SelectedFare } from '../../../../DesktopFlight/types';
import { FlightClass, ResultsLegFragment } from '@websky/graphql';

export const getFlightMinPrice = (flight: ResultsLegFragment) => {
	let result: number = null;

	flight.pricesForFareGroups.forEach(group => {
		if (group.prices && group.prices.length) {
			if (group.prices[0].price && (result === null || result > group.prices[0].price.amount)) {
				result = group.prices[0].price.amount;
			}
		}
	});

	return result;
};

export const getIataCompare = (flight: ResultsLegFragment, iata: string) => {
	return flight.segments[0].segment?.operatingAirline?.iata === iata ? -1 : 1;
};

export const sortFlights = (
	flights: ResultsLegFragment[],
	sorting: Sorting,
	iata?: string,
	sortResultPreferCurrentAirline?: boolean
): ResultsLegFragment[] => {
	switch (sorting) {
		case Sorting.BestFlight:
			return flights;

		case Sorting.Price:
			return flights.sort((a, b) => {
				const aPrice = getFlightMinPrice(a);
				const bPrice = getFlightMinPrice(b);

				const priceCompare = aPrice === bPrice ? 0 : aPrice > bPrice ? 1 : -1;

				if (sortResultPreferCurrentAirline) {
					const aIata = getIataCompare(a, iata);
					const bIata = getIataCompare(b, iata);

					if (aIata === bIata) {
						return priceCompare;
					}

					return aIata > bIata ? 1 : -1;
				}

				return priceCompare;
			});

		case Sorting.Departure:
			return flights.sort((a, b) => {
				const aDate = parseISO(
					`${a.segments[0].segment.departure.date}T${a.segments[0].segment.departure.time}`
				);
				const bDate = parseISO(
					`${b.segments[0].segment.departure.date}T${b.segments[0].segment.departure.time}`
				);

				if (isEqual(aDate, bDate)) {
					return 0;
				}

				return isAfter(aDate, bDate) ? 1 : -1;
			});

		case Sorting.Arrival:
			return flights.sort((a, b) => {
				const aDate = parseISO(
					`${a.segments[a.segments.length - 1].segment.arrival.date}T${
						a.segments[a.segments.length - 1].segment.arrival.time
					}`
				);
				const bDate = parseISO(
					`${b.segments[b.segments.length - 1].segment.arrival.date}T${
						b.segments[b.segments.length - 1].segment.arrival.time
					}`
				);

				if (isEqual(aDate, bDate)) {
					return 0;
				}

				return isAfter(aDate, bDate) ? 1 : -1;
			});

		case Sorting.FlightTime:
			return flights.sort((a, b) => {
				const aPrice = getDurationInSeconds(getFlightDuration(a.segments));
				const bPrice = getDurationInSeconds(getFlightDuration(b.segments));

				if (aPrice === bPrice) {
					return 0;
				}

				return aPrice > bPrice ? 1 : -1;
			});

		default:
			return flights;
	}
};

const isCompatibleWithSelectedFlights = (
	fare: ResultsLegFragment['pricesForFareGroups'][0],
	selectedFlightIds: Set<string>
) => {
	if (!selectedFlightIds.size) {
		return true;
	}

	return fare.prices?.some(price => selectedFlightIds.has(price.flight.id)) ?? false;
};

const isFareHasPrices = (fare: ResultsLegFragment['pricesForFareGroups'][0]) => fare.prices && fare.prices.length > 0;

const getSelectedFlightIds = (selectedFares: SelectedFare[] = []) =>
	new Set((selectedFares[selectedFares.length - 1]?.prices ?? []).map(price => price.flight.id));

export const hasBusinessClassFares = (flight: ResultsLegFragment, selectedFares: SelectedFare[] = []) => {
	const selectedFlightIds = getSelectedFlightIds(selectedFares);

	return flight.pricesForFareGroups.some(
		fare =>
			fare.fareFamily?.category === FlightClass.Business &&
			isFareHasPrices(fare) &&
			isCompatibleWithSelectedFlights(fare, selectedFlightIds)
	);
};

export const hasOnlyBusinessClassFares = (flight: ResultsLegFragment, selectedFares: SelectedFare[] = []) => {
	const selectedFlightIds = getSelectedFlightIds(selectedFares);
	const compatibleFareGroups = flight.pricesForFareGroups.filter(
		fare => isCompatibleWithSelectedFlights(fare, selectedFlightIds) && isFareHasPrices(fare)
	);

	return (
		compatibleFareGroups.length > 0 &&
		compatibleFareGroups.every(fare => fare.fareFamily?.category === FlightClass.Business)
	);
};

export const filterFlightsByType = (
	flights: ResultsLegFragment[],
	directOnly: boolean = false,
	businessClass: boolean = false,
	selectedFares: SelectedFare[] = []
) => {
	let filteredFlights = flights;

	if (directOnly) {
		filteredFlights = filteredFlights.filter(flight => flight.segments.length <= 1);
	}

	if (businessClass) {
		filteredFlights = filteredFlights.filter(flight => hasBusinessClassFares(flight, selectedFares));
	}

	return filteredFlights;
};
